import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';

export default function Settings({ user }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [profile, setProfile] = useState({});
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [supportForm, setSupportForm] = useState({
    showForm: false,
    subject: '',
    message: '',
    loading: false,
    response: ''
  });

  useEffect(() => {
    async function fetchProfile() {
      const { data } = await supabase.from('users').select('*').eq('id', user.id).single();
      setProfile(data || {});
      setForm(data || {});
    }
    fetchProfile();
  }, [user.id]);

  async function handleSave() {
    setMsg('');
    const { error } = await supabase.from('users').update({ 
      name: form.name, 
      email: form.email, 
      phone: form.phone,
      sms_opt_in: form.sms_opt_in 
    }).eq('id', user.id);
    if (error) setMsg(error.message);
    else {
      setProfile(form);
      setEditMode(false);
      setMsg('Profile updated!');
    }
  }

  async function handlePasswordUpdate() {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMsg('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordMsg('New password must be at least 6 characters long');
      return;
    }

    setPasswordLoading(true);
    setPasswordMsg('');

    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordForm.newPassword
      });

      if (error) {
        setPasswordMsg(error.message);
      } else {
        setPasswordMsg('Password updated successfully!');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      setPasswordMsg('An error occurred. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  }

  async function handleSupportTicket() {
    if (!supportForm.subject || !supportForm.message) {
      setSupportForm(prev => ({
        ...prev,
        response: 'Please fill in both subject and message'
      }));
      return;
    }

    setSupportForm(prev => ({ ...prev, loading: true, response: '' }));

    try {
      const response = await fetch('/api/create-support-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: supportForm.subject,
          message: supportForm.message,
          userEmail: user.email
        })
      });

      const data = await response.json();

      if (data.success) {
        setSupportForm({
          showForm: false,
          subject: '',
          message: '',
          loading: false,
          response: 'Support ticket created successfully!'
        });
      } else {
        setSupportForm(prev => ({
          ...prev,
          loading: false,
          response: data.message || 'Failed to create support ticket'
        }));
      }
    } catch (error) {
      setSupportForm(prev => ({
        ...prev,
        loading: false,
        response: 'An error occurred. Please try again.'
      }));
    }
  }

  return (
    <div style={{ color: '#fff', maxWidth: 480, margin: '60px auto' }}>
      <h2 style={{textAlign:'center'}}>Settings</h2>
      
      {/* Total Savings Section */}
      <div style={{
        background: 'rgba(0,0,0,0.2)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '8px',
        padding: '16px',
        textAlign: 'center',
        marginBottom: '18px'
      }}>
        <div style={{fontSize: '0.9rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '1px'}}>Total Saved</div>
        <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#4caf50', marginTop: '4px'}}>
          ${profile.total_savings ? profile.total_savings.toFixed(2) : '0.00'}
        </div>
      </div>

      {/* Navigation Buttons */}
      <button onClick={() => navigate('/orders')} style={{width:'100%', background:'none', color:'#fff', border:'1px solid #fff', borderRadius:8, padding:'12px 0', fontSize:'1.05rem', cursor:'pointer', marginBottom:18}}>
        Orders Page
      </button>

      {/* Profile Section */}
      <button onClick={() => setExpanded(!expanded)} style={{width:'100%', background:'none', color:'#fff', border:'1px solid #fff', borderRadius:8, padding:'12px 0', fontSize:'1.05rem', cursor:'pointer', marginBottom:18}}>
        Profile {expanded ? '▲' : '▼'}
      </button>

      {expanded && (
        <div style={{marginTop:18, marginBottom:18}}>
          {!editMode ? (
            <>
              <table style={{width:'100%', marginBottom:18}}>
                <tbody>
                  <tr><td style={{padding:'6px 0'}}>Name</td><td>{profile.name || <span style={{color:'#aaa'}}>Not set</span>}</td></tr>
                  <tr><td style={{padding:'6px 0'}}>Email</td><td>{profile.email || <span style={{color:'#aaa'}}>Not set</span>}</td></tr>
                  <tr><td style={{padding:'6px 0'}}>Phone</td><td>{profile.phone || <span style={{color:'#aaa'}}>Not set</span>}</td></tr>
                </tbody>
              </table>
              <button onClick={() => setEditMode(true)} style={{background:'none', color:'#fff', border:'1px solid #fff', borderRadius:8, padding:'10px 18px', cursor:'pointer'}}>Edit Profile</button>
            </>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input id="name" value={form.name || ''} onChange={e => setForm({...form, name:e.target.value})} />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input id="email" value={form.email || ''} onChange={e => setForm({...form, email:e.target.value})} />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input id="phone" value={form.phone || ''} onChange={e => setForm({...form, phone:e.target.value})} />
              </div>

              <div style={{display: 'flex', alignItems: 'center', marginBottom: '18px', marginTop: '12px'}}>
                <input
                  type="checkbox"
                  id="sms_opt_in"
                  checked={!!form.sms_opt_in}
                  onChange={e => setForm({ ...form, sms_opt_in: e.target.checked })}
                  style={{marginRight: '10px', width: '18px', height: '18px', cursor: 'not-allowed'}}
                  disabled
                />
                <label htmlFor="sms_opt_in" style={{cursor: 'not-allowed', color: '#888'}}>
                  Receive SMS notifications for order updates. (Coming Soon)
                </label>
              </div>

              <div style={{display:'flex', gap:12}}>
                <button onClick={handleSave} style={{background:'none', color:'#fff', border:'1px solid #fff', borderRadius:8, padding:'10px 18px', cursor:'pointer', flex:1}}>Save</button>
                <button onClick={() => { setEditMode(false); setForm(profile); setMsg(''); }} style={{background:'none', color:'#fff', border:'1px solid #fff', borderRadius:8, padding:'10px 18px', cursor:'pointer', flex:1}}>Cancel</button>
              </div>
            </>
          )}
          {msg && <div style={{marginTop:12}}>{msg}</div>}
        </div>
      )}

      {/* Password Section */}
      <button onClick={() => setPasswordForm(prev => ({ ...prev, showForm: !prev.showForm }))} style={{width:'100%', background:'none', color:'#fff', border:'1px solid #fff', borderRadius:8, padding:'12px 0', fontSize:'1.05rem', cursor:'pointer'}}>
        Change Password {passwordForm.showForm ? '▲' : '▼'}
      </button>

      {passwordForm.showForm && (
        <div style={{marginTop:18}}>
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              id="newPassword"
              type="password"
              value={passwordForm.newPassword}
              onChange={e => setPasswordForm({...passwordForm, newPassword: e.target.value})}
              placeholder="Enter new password"
              disabled={passwordLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={e => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
              placeholder="Confirm new password"
              disabled={passwordLoading}
            />
          </div>

          <button
            onClick={handlePasswordUpdate}
            disabled={passwordLoading}
            style={{
              width: '100%',
              background: 'none',
              color: passwordLoading ? '#666' : '#fff',
              border: `1px solid ${passwordLoading ? '#666' : '#fff'}`,
              borderRadius: 8,
              padding: '10px 24px',
              cursor: passwordLoading ? 'not-allowed' : 'pointer',
              marginTop: '12px'
            }}
          >
            {passwordLoading ? 'Updating...' : 'Update Password'}
          </button>

          {passwordMsg && (
            <div style={{ 
              marginTop: '15px',
              padding: '10px',
              borderRadius: '6px',
              backgroundColor: passwordMsg.includes('successfully') ? '#4CAF50' : '#ff4757',
              color: 'white',
              textAlign: 'center'
            }}>
              {passwordMsg}
            </div>
          )}
        </div>
      )}

      {/* Support Ticket Section */}
      <button 
        onClick={() => setSupportForm(prev => ({ ...prev, showForm: !prev.showForm }))}
        style={{
          width: '100%',
          background: 'none',
          color: '#fff',
          border: '1px solid #fff',
          borderRadius: 8,
          padding: '12px 0',
          fontSize: '1.05rem',
          cursor: 'pointer',
          marginTop: '18px'
        }}
      >
        Contact Support {supportForm.showForm ? '▲' : '▼'}
      </button>

      {supportForm.showForm && (
        <div style={{ marginTop: '18px' }}>
          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <input
              id="subject"
              value={supportForm.subject}
              onChange={e => setSupportForm(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Enter subject"
              disabled={supportForm.loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              value={supportForm.message}
              onChange={e => setSupportForm(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Describe your issue..."
              rows={4}
              style={{ resize: 'vertical' }}
              disabled={supportForm.loading}
            />
          </div>

          <button
            onClick={handleSupportTicket}
            disabled={supportForm.loading}
            style={{
              width: '100%',
              background: 'none',
              color: supportForm.loading ? '#666' : '#fff',
              border: `1px solid ${supportForm.loading ? '#666' : '#fff'}`,
              borderRadius: 8,
              padding: '10px 24px',
              cursor: supportForm.loading ? 'not-allowed' : 'pointer',
              marginTop: '12px'
            }}
          >
            {supportForm.loading ? 'Submitting...' : 'Submit Ticket'}
          </button>

          {supportForm.response && (
            <div style={{ 
              marginTop: '15px',
              padding: '10px',
              borderRadius: '6px',
              backgroundColor: supportForm.response.includes('successfully') ? '#4CAF50' : '#ff4757',
              color: 'white',
              textAlign: 'center'
            }}>
              {supportForm.response}
            </div>
          )}
        </div>
      )}
    </div>
  );
}